import type {
  NativeDeepLinkPayload,
  NativeDeepLinkResult,
} from "%/webviewBridge";
import { getPushDeepLinkFallbackPath } from "%/pushDeepLinkPolicy";

export const normalizeDeepLinkPayload = (
  payloadOrPath: NativeDeepLinkPayload | string,
  query?: string,
  type?: string,
): NativeDeepLinkPayload => {
  if (typeof payloadOrPath === "string") {
    return {
      path: payloadOrPath,
      query: query || "",
      type: type || "",
    };
  }

  return {
    ...payloadOrPath,
    query: payloadOrPath.query || "",
  };
};

export const buildDeepLinkTarget = (
  payload: NativeDeepLinkPayload,
): string => {
  const path = payload.path?.trim() || getPushDeepLinkFallbackPath(payload.type);
  if (!path) {
    throw new Error(`Unsupported push deep link type: ${payload.type || ""}`);
  }

  const queryString = normalizeQuery(payload.query);
  return queryString ? `${path}?${queryString}` : path;
};

export const successDeepLinkResult = (
  payload: NativeDeepLinkPayload,
  target: string,
  duplicate = false,
): NativeDeepLinkResult => ({
  success: true,
  deepLinkId: payload.deepLinkId,
  target,
  duplicate,
});

export const failedDeepLinkResult = (
  payload: NativeDeepLinkPayload,
  error: unknown,
): NativeDeepLinkResult => ({
  success: false,
  deepLinkId: payload.deepLinkId,
  error: error instanceof Error ? error.message : String(error),
});

const normalizeQuery = (
  query?: NativeDeepLinkPayload["query"],
): string => {
  if (!query) {
    return "";
  }

  if (typeof query === "string") {
    return query.startsWith("?") ? query.slice(1) : query;
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  return params.toString();
};
